/**
 * Session: auth state, current user, live wallet balance, client bootstrap config and the realtime connection.
 */
import { create } from 'zustand';
import type { AuthResponse, MeDto, NotificationDto, UserSettings } from '@cardhub/protocol';
import { api, ApiError, getAccessToken, notifySessionLost, onSessionExpired, refreshSession, refreshWasUnreachable, setAccessToken } from '../api/client';
import { RealtimeClient, type WsStatus } from '../api/ws';
import { storeRefreshToken } from '../native/platform';
import { sfx } from '../lib/sound';
import { applySettings, useSettings } from './settings';
import { toast } from './ui';
import { prefetchCatalog } from '../offline/catalog';
import { flushClaims, pendingDelta } from '../offline/claims';

export interface BootConfig {
  flags: Record<string, boolean>;
  config: Record<string, unknown>;
  announcements: { id: string; title: string; body: string; level: string }[];
  events: { code: string; name: string; description: string; endsAt: string; banner: string | null; color: string | null }[];
  maintenance: boolean;
  avatars: string[];
}

interface SessionState {
  status: 'loading' | 'anon' | 'authed';
  user: MeDto | null;
  balance: number;
  boot: BootConfig | null;
  ws: WsStatus;
  unread: number;
  lastNotification: NotificationDto | null;
  /** Signed in from the cached profile because the server was unreachable at launch. */
  offlineStart: boolean;
  init(): Promise<void>;
  applyAuth(res: AuthResponse): void;
  refreshMe(): Promise<void>;
  logout(): Promise<void>;
  setBalance(n: number): void;
  loadBoot(): Promise<void>;
  flag(key: string): boolean;
}

const USER_CACHE = 'th.cachedUser';

/** The last known profile, kept so the app can open and play offline when the server cannot be reached. */
function cacheUser(u: MeDto | null): void {
  try {
    if (u) localStorage.setItem(USER_CACHE, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE);
  } catch {
    /* storage unavailable */
  }
}

function cachedUser(): MeDto | null {
  try {
    return JSON.parse(localStorage.getItem(USER_CACHE) ?? 'null') as MeDto | null;
  } catch {
    return null;
  }
}

export const realtime = new RealtimeClient(async () => {
  const have = getAccessToken();
  if (have) return have;
  const t = await refreshSession();
  // An app opened offline from the saved profile has no token yet; if the server now refuses the saved login,
  // the player must sign in again (their queued offline games stay on the device and settle after that).
  if (!t && !refreshWasUnreachable() && useSession.getState().status === 'authed') notifySessionLost();
  return t?.accessToken ?? null;
});

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,
  balance: 0,
  boot: null,
  ws: 'idle',
  unread: 0,
  lastNotification: null,
  offlineStart: false,

  async init() {
    realtime.onStatus((s) => {
      set({ ws: s });
      if (s === 'open' && get().status === 'authed') {
        // Back online: pay out offline matches and, if we started from the cached profile, fetch the real one.
        if (get().offlineStart) {
          set({ offlineStart: false });
          void get().refreshMe().catch(() => undefined);
          void get().loadBoot();
        }
        void flushClaims();
        prefetchCatalog();
      }
    });
    window.addEventListener('online', () => {
      void flushClaims();
      if (get().status === 'authed') prefetchCatalog();
    });
    realtime.on('wallet.balance.v1', (d) => {
      const { balance, amount } = d as { balance: number; amount: number };
      set({ balance: balance + pendingDelta(get().user?.id) });
      if (amount > 0) sfx.coin();
    });
    realtime.on('notify.v1', (d) => {
      const n = d as NotificationDto;
      set({ unread: get().unread + 1, lastNotification: n });
      sfx.notify();
      toast(`${n.title}${n.body ? ' — ' + n.body : ''}`, 'info');
    });
    realtime.on('session.replaced.v1', () => toast('You signed in somewhere else. This tab is now inactive.', 'error'));
    realtime.on('sys.announcement.v1', (d) => toast((d as { title: string }).title, 'info'));
    onSessionExpired(() => {
      if (get().status === 'authed') {
        toast('Your session ended. Please sign in again.', 'error');
        void get().logout();
      }
    });
    void get().loadBoot();
    const tokens = await refreshSession();
    if (!tokens) {
      // Server unreachable (not a rejected session): open from the cached profile so offline play works.
      const cached = refreshWasUnreachable() ? cachedUser() : null;
      if (cached) {
        set({ status: 'authed', user: cached, balance: cached.coins + pendingDelta(cached.id), offlineStart: true });
        syncSettingsFromServer(cached.settings);
        realtime.start();
        return;
      }
      set({ status: 'anon' });
      return;
    }
    try {
      await get().refreshMe();
      set({ status: 'authed' });
      realtime.start();
    } catch (e) {
      const cached = e instanceof ApiError && e.code === 'NETWORK' ? cachedUser() : null;
      if (cached) {
        set({ status: 'authed', user: cached, balance: cached.coins + pendingDelta(cached.id), offlineStart: true });
        realtime.start();
        return;
      }
      setAccessToken(null);
      set({ status: 'anon' });
    }
  },

  applyAuth(res) {
    setAccessToken(res.tokens.accessToken);
    void storeRefreshToken(res.tokens.refreshToken);
    cacheUser(res.user);
    set({ status: 'authed', user: res.user, balance: res.user.coins + pendingDelta(res.user.id) });
    syncSettingsFromServer(res.user.settings);
    realtime.start();
    void get().loadBoot();
  },

  async refreshMe() {
    const me = await api.get<MeDto>('/api/v1/me');
    cacheUser(me);
    set({ user: me, balance: me.coins + pendingDelta(me.id) });
    syncSettingsFromServer(me.settings);
    api
      .get<{ unread: number }>('/api/v1/notifications')
      .then((n) => set({ unread: n.unread }))
      .catch(() => undefined);
  },

  async logout() {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      /* ignore */
    }
    realtime.stop();
    setAccessToken(null);
    await storeRefreshToken(null);
    cacheUser(null);
    set({ status: 'anon', user: null, balance: 0, unread: 0 });
  },

  setBalance(n) {
    set({ balance: n + pendingDelta(get().user?.id) });
  },

  async loadBoot() {
    try {
      const b = await api.get<BootConfig>('/api/v1/config');
      set({ boot: b });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MAINTENANCE') set({ boot: { flags: {}, config: {}, announcements: [], events: [], maintenance: true, avatars: [] } });
    }
  },

  flag(key) {
    return get().boot?.flags[key] ?? false;
  },
}));

function syncSettingsFromServer(server: UserSettings): void {
  useSettings.getState().replace(server);
  applySettings(server);
}

/** Persists a settings change locally and (when signed in) on the server. */
export async function saveSettings(patch: Partial<UserSettings>): Promise<void> {
  useSettings.getState().set(patch);
  applySettings(useSettings.getState().settings);
  if (useSession.getState().status === 'authed') {
    try {
      await api.patch('/api/v1/me/settings', patch);
    } catch {
      toast('Could not save settings to your account', 'error');
    }
  }
}
